/**
 * "DOES AN OPEN PANEL OWN THIS ESC?" for the canvas's full-screen Esc
 * (workspace-canvas.tsx, rule 14; 2026-10-03).
 *
 * WHY. Esc on the page leaves the full screen on view, after anything else
 * that owns the key. Since 2026-10-03 the notifications card can be open
 * over a full-screen app (the Notifications row of the bar's ⋯), and a panel
 * closes itself on Esc without claiming the key (panel-shell.tsx), so one
 * press closed the card AND left full screen.
 *
 * WHY IT TAKES TWO LISTENERS. The panel's handler and the canvas's are both
 * on `document`, in the bubble phase, so the browser runs them in the order
 * they were added: the canvas's first when the panel opened after full
 * screen began (the notifications card), the panel's first when it was
 * already open (the account panel left open, then the green dot). In the
 * second order the panel has closed itself before the canvas is asked: a
 * keydown is a discrete event, so React commits the close in the microtask
 * between the two listeners, and by then neither the document nor the
 * panels' open-state mirror says a panel was open. So the question is
 * answered at the START of the key event, in the capture phase, which runs
 * before every bubble listener whatever the order.
 *
 * WHY THE DOCUMENT AND NOT THE MIRROR. `openWorkspacePanel` sets the mirror
 * eagerly and nothing clears it when the panel it names is not mounted (a
 * signed-out viewer has no left panel, and Search still asks for it). A
 * stale "open" would leave Esc dead in full screen. An open desktop card
 * marks itself (`data-ws-panel-open`, panel-shell.tsx); that is asked here.
 */

/** The mark an open desktop panel card carries (panel-shell.tsx). */
export const OPEN_PANEL_SELECTOR = '[data-ws-panel-open]';

export interface PanelEscWatch {
  /** True when a panel card was open as this Esc press began, or is open now. */
  owned: () => boolean;
  /** Take the capture listener off. */
  stop: () => void;
}

/**
 * Start watching. Call `owned()` from a bubble-phase keydown handler; call
 * `stop()` when that handler is removed.
 */
export function watchPanelOwnsEsc(doc: Document = document): PanelEscWatch {
  let openAsPressBegan = false;
  const onKeyCapture = (e: KeyboardEvent) => {
    // Every keydown re-answers, so one press never inherits the last one's.
    openAsPressBegan = e.key === 'Escape' && !!doc.querySelector(OPEN_PANEL_SELECTOR);
  };
  doc.addEventListener('keydown', onKeyCapture, true);
  return {
    owned: () => openAsPressBegan || !!doc.querySelector(OPEN_PANEL_SELECTOR),
    stop: () => doc.removeEventListener('keydown', onKeyCapture, true),
  };
}
